/* ----------------------------------------------------------------------------
 * BRO "Patient Data Collection" visit form — "shared" chapter set (11 chapters)
 *
 * Reuses several BEX chapters verbatim and adds the BRO/OSA-specific ones.
 * Verified against the live app (USZ-BRO-00001) + firestore_backup.json:
 *   vitalSigns:   bloodPressure{1,2,3}, temperature, heartRate{1,2,3}, sp02
 *   smoking:      OBJECT { habits, cigsPerDay, packYears, started }  (flat — not the BEX array)
 *   questionnaire: essTotalScore, fosqTotalScore  (flat)
 *   osa:          { circumference{hip,waist,neck}, poligraphyDate, ahi,
 *                   apneas*(Obstruction/Central[+Percent]), odi, saturationTime,
 *                   cpapTrialAccepted }
 *
 * NOTE: TRX currently falls back to this set; build visit/trx.js once its
 * chapters are captured (questionnaire/osa variants may differ).
 * ------------------------------------------------------------------------- */
import {
  yesNo,
  anthropometrics,
  eatingDrinking,
  medication,
  morbidities,
  lungFunction,
  breathAnalysis,
} from './bex'

export const vitalSigns = [
  {
    type: 'group',
    name: 'bloodPressure',
    children: [
      { type: 'text', name: '1', label: 'Blood pressure 1 (mmHg)' },
      { type: 'text', name: '2', label: 'Blood pressure 2 (mmHg)' },
      { type: 'text', name: '3', label: 'Blood pressure 3 (mmHg)' },
    ],
  },
  { type: 'text', name: 'temperature', label: 'Temperature (°C)' },
  {
    type: 'group',
    name: 'heartRate',
    children: [
      { type: 'text', name: '1', label: 'Heart rate 1' },
      { type: 'text', name: '2', label: 'Heart rate 2' },
      { type: 'text', name: '3', label: 'Heart rate 3' },
    ],
  },
  { type: 'text', name: 'sp02', label: 'Sp02 (%)' },
]

// BRO smoking is a flat group (unlike the BEX repeatable array).
const smoking = [
  {
    type: 'group',
    name: 'smoking',
    children: [
      {
        type: 'select',
        name: 'habits',
        label: 'Smoking habits',
        options: {
          '': '',
          neverSmoker: 'Never-smoker',
          smoker: 'Smoker',
          exSmoker: 'Ex-smoker',
        },
      },
      {
        type: 'text',
        name: 'cigsPerDay',
        label: 'Number of cigarettes per day',
      },
      { type: 'text', name: 'packYears', label: 'Pack years' },
      { type: 'text', name: 'started', label: 'Start of smoking' },
    ],
  },
]

const questionnaire = [
  { type: 'text', name: 'essTotalScore', label: 'ESS total score' },
  { type: 'text', name: 'fosqTotalScore', label: 'FOSQ total score' },
]

const osaSpecifics = [
  {
    type: 'group',
    name: 'osa',
    children: [
      {
        type: 'group',
        name: 'circumference',
        children: [
          { type: 'text', name: 'hip', label: 'Hip circumference (cm)' },
          { type: 'text', name: 'waist', label: 'Waist circumference (cm)' },
          { type: 'text', name: 'neck', label: 'Neck circumference (cm)' },
        ],
      },
      {
        type: 'text',
        name: 'poligraphyDate',
        label: 'Date of respiratory polygraphy',
      },
      { type: 'text', name: 'ahi', label: 'AHI (number/h)' },
      {
        type: 'text',
        name: 'apneasObstruction',
        label: 'Number of obstructive apneas',
      },
      {
        type: 'text',
        name: 'apneasObstructionPercent',
        label: 'Obstructive apneas of total apneas (%)',
      },
      {
        type: 'text',
        name: 'apneasCentral',
        label: 'Number of central apneas',
      },
      {
        type: 'text',
        name: 'apneasCentralPercent',
        label: 'Central apneas of total apneas (%)',
      },
      { type: 'text', name: 'odi', label: 'ODI (>=3%) (number/h)' },
      { type: 'text', name: 'saturationTime', label: 'Time < 90 % saturation' },
      {
        type: 'select',
        name: 'cpapTrialAccepted',
        label: 'CPAP trial accepted',
        options: yesNo,
      },
    ],
  },
]

// "Visit completion" chapter — a single confirmation control in the original.
const finish = [{ component: 'h4', children: 'Visit 1 Completion' }]

export default {
  chapters: [
    {
      key: 'anthropometrics',
      name: 'Anthropometrics',
      schema: anthropometrics,
    },
    { key: 'vitalSigns', name: 'Vital signs', schema: vitalSigns },
    {
      key: 'eatingDrinking',
      name: 'Eating & drinking habits',
      schema: eatingDrinking,
    },
    { key: 'smokingHistory', name: 'Smoking history', schema: smoking },
    { key: 'morbidities', name: 'Morbidities', schema: morbidities },
    { key: 'medication', name: 'Medication', schema: medication },
    { key: 'questionnaire', name: 'Questionnaire', schema: questionnaire },
    { key: 'lungFunction', name: 'Lung Function', schema: lungFunction },
    {
      key: 'osaSpecifics',
      name: 'OSA specifics & respiratory polygraphy',
      schema: osaSpecifics,
    },
    { key: 'breathAnalysis', name: 'Breath analysis', schema: breathAnalysis },
    { key: 'finish', name: 'Visit completion', schema: finish },
  ],
}
